import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MaskedLine } from "../Reveal";

export const Hero = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 110]);
  const yMark = useTransform(scrollY, [0, 700], [0, -60]);
  const rotate = useTransform(scrollY, [0, 700], [0, 6]);

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <motion.span
        style={{ y: yMark }}
        aria-hidden="true"
        className="text-outline pointer-events-none absolute -bottom-8 left-0 select-none font-serif text-[24vw] font-bold leading-none tracking-tight"
      >
        LOCKA
      </motion.span>

      <div className="relative z-10 mx-auto grid w-full max-w-[1500px] items-center gap-14 px-6 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.35}>
            <span className="mb-7 inline-flex items-center gap-3 text-[0.68rem] font-bold uppercase tracking-[0.34em] text-burgundy">
              <span className="h-px w-10 bg-gold" /> Maison de Chaussettes · Est. MMXXIV
            </span>
          </MaskedLine>
          <h1 className="font-serif text-[13.5vw] font-medium leading-[0.98] tracking-tight text-navy sm:text-6xl lg:text-[5.6rem]" data-testid="hero-headline">
            <MaskedLine delay={0.5}>Step Into</MaskedLine>
            <MaskedLine delay={0.65}>
              <em className="text-gold">Timeless</em> Style<span className="text-burgundy">.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.85}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-navy/65 md:text-lg" data-testid="hero-subcopy">
              Vintage soul. Modern stride. Locka weaves heritage patterns and contemporary nerve into designer socks for those who dress from the ground up.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={() => navigate("/shop")}
              data-testid="hero-cta-shop"
              className="group relative overflow-hidden bg-navy px-10 py-4 text-[0.72rem] font-bold uppercase tracking-[0.3em] text-cream"
            >
              <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative flex items-center gap-3 transition-colors duration-500 group-hover:text-[#0B101E]">
                Step Into Style <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </span>
            </button>
            <button
              onClick={() => window.__lenis?.scrollTo("#story", { offset: -70 })}
              data-testid="hero-cta-story"
              className="group flex items-center gap-3 text-[0.72rem] font-bold uppercase tracking-[0.3em] text-navy"
            >
              <span className="border-b border-gold pb-1 transition-colors duration-300 group-hover:text-gold">Explore the Story</span>
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-14 flex items-center gap-8 text-[0.62rem] font-semibold uppercase tracking-[0.26em] text-navy/45"
          >
            <span>200-Needle Knit</span>
            <span className="h-3 w-px bg-navy/20" />
            <span>Hand-Linked Toes</span>
            <span className="h-3 w-px bg-navy/20" />
            <span>Numbered Drops</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <div aria-hidden="true" className="absolute -inset-10 -z-0 rounded-full bg-gold/10 blur-[90px]" />
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.55, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yImg, rotate }} className="clip-frame relative aspect-[4/5] overflow-hidden shadow-[0_60px_120px_-30px_rgba(0,0,0,0.7)]">
              <img src="/images/hero-main.jpg" alt="Locka luxury socks in gold thread spotlight" className="h-full w-full object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -left-8 -top-8 hidden h-28 w-28 items-center justify-center rounded-full border border-gold/60 bg-cream/80 backdrop-blur md:flex"
            >
              <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow p-2">
                <defs>
                  <path id="circlePath" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                </defs>
                <text className="fill-navy text-[9.5px] font-semibold uppercase" style={{ letterSpacing: "2.6px" }}>
                  <textPath href="#circlePath">Every step is couture · every step is couture ·</textPath>
                </text>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.55, duration: 0.8 }}
              className="absolute -bottom-7 -right-3 bg-burgundy px-6 py-4 text-white shadow-xl md:-right-8"
            >
              <p className="font-serif text-2xl italic leading-none">The Centennial</p>
              <p className="mt-1.5 text-[0.6rem] font-bold uppercase tracking-[0.26em] text-gold-soft">Limited · 1 of 250</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4 text-navy/40" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};
